import { create } from 'zustand';
import {
  getCouponType,
  getOrderStatus,
  getProductShape,
  getProductStatus,
  getProductType,
  getRefundResolution,
  getRefundStatus,
  getServiceType,
  getShippingStatus,
} from '@/services/ant-design-pro/common';

export interface OrderStatusItem {
  dictLabel: string;
  dictValue: string;
}

export const transformDict = (data: any[]): OrderStatusItem[] => {
  if (!Array.isArray(data)) return [];
  return data.map((item) => ({
    dictLabel: item.dictLabel,
    dictValue: item.dictValue,
  }));
};

export interface ProductItem {
  id: string;
  prodName: string;
  prodType: string;
  prodShape: string;
  prodStatus: string;
}

interface CommonStoreState {
  serviceType: OrderStatusItem[];
  productType: OrderStatusItem[];
  productShape: OrderStatusItem[];
  productStatus: OrderStatusItem[];
  orderStatus: OrderStatusItem[];
  shippingStatus: OrderStatusItem[];
  couponType: OrderStatusItem[];
  refundStatus: OrderStatusItem[];
  refundResolution: OrderStatusItem[];
  initializing: boolean;
  initialized: boolean;
  setServiceType: (list: OrderStatusItem[]) => void;
  setProductType: (list: OrderStatusItem[]) => void;
  setProductShape: (list: OrderStatusItem[]) => void;
  setProductStatus: (list: OrderStatusItem[]) => void;
  setOrderStatus: (list: OrderStatusItem[]) => void;
  setShippingStatus: (list: OrderStatusItem[]) => void;
  setCouponType: (list: OrderStatusItem[]) => void;
  setRefundStatus: (list: OrderStatusItem[]) => void;
  setRefundResolution: (list: OrderStatusItem[]) => void;
  initCommonData: () => Promise<void>;
}

export const useCommonStore = create<CommonStoreState>((set, get) => ({
  serviceType: [],
  productType: [],
  productShape: [],
  productStatus: [],
  orderStatus: [],
  couponType: [],
  shippingStatus: [],
  refundStatus: [],
  refundResolution: [],
  initializing: false,
  initialized: false,
  setServiceType: (list) => set({ serviceType: list }),
  setProductType: (list) => set({ productType: list }),
  setProductShape: (list) => set({ productShape: list }),
  setProductStatus: (list) => set({ productStatus: list }),
  setOrderStatus: (list) => set({ orderStatus: list }),
  setShippingStatus: (list) => set({ shippingStatus: list }),
  setCouponType: (list) => set({ couponType: list }),
  setRefundStatus: (list) => set({ refundStatus: list }),
  setRefundResolution: (list) => set({ refundResolution: list }),
  initCommonData: async () => {
    // Avoid duplicate initialization
    const { initializing, initialized } = get();
    if (initializing || initialized) return;
    set({ initializing: true });
    try {
      const [
        serviceTypeRes,
        productTypeRes,
        productShapeRes,
        productStatusRes,
        orderStatusRes,
        shippingStatusRes,
        couponTypeRes,
        refundStatusRes,
        refundResolutionRes,
      ] = await Promise.all([
        getServiceType(),
        getProductType(),
        getProductShape(),
        getProductStatus(),
        getOrderStatus(),
        getShippingStatus(),
        getCouponType(),
        getRefundStatus(),
        getRefundResolution(),
      ]);
      const serviceTypeRows = (serviceTypeRes as any)?.rows;
      const productTypeRows = (productTypeRes as any)?.rows;
      const productShapeRows = (productShapeRes as any)?.rows;
      const productStatusRows = (productStatusRes as any)?.rows;
      const orderStatusRows = (orderStatusRes as any)?.rows;
      const shippingStatusRows = (shippingStatusRes as any)?.rows;
      const couponTypeRows = (couponTypeRes as any)?.rows;
      const refundStatusRows = (refundStatusRes as any)?.rows;
      const refundResolutionRows = (refundResolutionRes as any)?.rows;

      if (Array.isArray(serviceTypeRows)) {
        set({ serviceType: transformDict(serviceTypeRows) });
      }
      if (Array.isArray(productTypeRows)) {
        set({ productType: transformDict(productTypeRows) });
      }
      if (Array.isArray(productShapeRows)) {
        set({ productShape: transformDict(productShapeRows) });
      }
      if (Array.isArray(productStatusRows)) {
        set({ productStatus: transformDict(productStatusRows) });
      }
      if (Array.isArray(orderStatusRows)) {
        set({ orderStatus: transformDict(orderStatusRows) });
      }
      if (Array.isArray(shippingStatusRows)) {
        set({ shippingStatus: transformDict(shippingStatusRows) });
      }
      if (Array.isArray(couponTypeRows)) {
        set({ couponType: transformDict(couponTypeRows) });
      }
      if (Array.isArray(refundStatusRows)) {
        set({ refundStatus: transformDict(refundStatusRows) });
      }
      if (Array.isArray(refundResolutionRows)) {
        set({ refundResolution: transformDict(refundResolutionRows) });
      }
      set({ initialized: true });
    } catch (_e) {
      // swallow error; consumers should handle empty store gracefully
    } finally {
      set({ initializing: false });
    }
  },
}));
